import { Section, Findings, Bullets, TechList, Prose, EvidenceBadge } from "./primitives";
import { ExecExtras } from "./TabsDeep";

export const ExecutiveTab = ({ r }) => {
  const e = r.executive_summary || {};
  const c = e.complexity || {};
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Section title="Overview" testid="exec-overview" className="lg:col-span-2">
        <p className="mb-3 font-mono text-xs uppercase tracking-widest text-[#d4af37]">{e.site_type}</p><Prose>{e.overview}</Prose>
      </Section>
      <Section title="Complexity" testid="exec-complexity">
        <p className="font-display text-5xl font-bold text-red-500">{c.score}<span className="text-xl text-neutral-500">/10</span></p>
        <p className="mt-1 font-mono text-sm uppercase tracking-widest">{c.level}</p>
        <div className="mt-3 h-1.5 bg-[#1f1f1f]"><div className="h-full bg-gradient-to-r from-red-900 to-red-500" style={{ width: `${(c.score || 0) * 10}%` }} /></div>
        <p className="mt-3 text-sm text-neutral-400">{c.rationale}</p>
      </Section>
      <Section title="Key Findings" testid="exec-key-findings" className="lg:col-span-2"><Findings items={e.key_findings} /></Section>
      <Section title="Next Steps" testid="exec-next-steps"><Bullets items={e.next_steps} /></Section>
      <Section title="Main Technologies" testid="exec-technologies" className="lg:col-span-3"><TechList items={e.main_technologies} /></Section>
      <Section title="Observations" testid="exec-observations" className="lg:col-span-2"><Findings items={e.observations} /></Section>
      <Section title="Risks & Limitations" testid="exec-risks"><Findings items={e.risks_limitations} /></Section>
      <ExecExtras e={e} />
    </div>
  );
};

const RouteTree = ({ routes }) => (
  <div className="space-y-1 font-mono text-sm">
    {(routes || []).map((rt, i) => (
      <div key={i} className="flex items-start justify-between gap-3 border-b border-[#161616] py-1.5" style={{ paddingLeft: `${(rt.depth || 0) * 18}px` }}>
        <span><span className="text-red-500">{rt.depth ? "└─ " : "● "}</span><span className="text-white">{rt.path}</span> <span className="text-neutral-500">· {rt.title}</span>
          {rt.purpose && <span className="block pl-5 text-xs text-neutral-500">{rt.purpose}</span>}</span>
        <EvidenceBadge label={rt.label} />
      </div>
    ))}
  </div>
);

const Layers = ({ layers }) => (
  <div className="space-y-2">
    {(layers || []).map((l, i) => (
      <div key={i} className="grid gap-2 border border-[#222] bg-[#0a0a0a] p-3 sm:grid-cols-[180px_1fr_auto] sm:items-center" style={{ borderLeft: `3px solid ${i === 0 ? "#dc2626" : i % 2 ? "#7f1d1d" : "#d4af37"}` }}>
        <p className="font-display text-sm font-semibold uppercase tracking-wider">{l.name}</p>
        <div className="flex flex-wrap gap-1.5">{(l.items || []).map((x) => <span key={x} className="border border-[#2a2a2a] px-2 py-0.5 font-mono text-[11px] text-neutral-300">{x}</span>)}</div>
        <EvidenceBadge label={l.label} />
      </div>
    ))}
  </div>
);

export const ArchitectureTab = ({ r }) => {
  const a = r.architecture || {};
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Section title="Architecture Overview" testid="arch-overview" className="lg:col-span-2"><Prose>{a.overview}</Prose>
        {a.rendering_strategy && <div className="mt-4"><p className="rea-label mb-2">Rendering strategy</p><Findings items={[a.rendering_strategy]} /></div>}
      </Section>
      {a.data_flow?.length > 0 && <Section title="Data Flow" testid="arch-data-flow" className="lg:col-span-2"><Findings items={a.data_flow} /></Section>}
      <Section title="System Layers (evidence-based diagram)" testid="arch-layers" className="lg:col-span-2"><Layers layers={a.layers} /></Section>
      <Section title="Page / Route Hierarchy" testid="arch-routes"><RouteTree routes={a.route_hierarchy} /></Section>
      <Section title="Component Relationships" testid="arch-components">
        <div className="space-y-3">{(a.component_relationships || []).map((c, i) => (
          <div key={i}><p className="font-mono text-sm text-[#d4af37]">&lt;{c.parent}&gt;</p>
            <div className="ml-3 mt-1 flex flex-wrap gap-1.5 border-l border-red-900 pl-3">{(c.children || []).map((x) => <span key={x} className="font-mono text-xs text-neutral-300">{x}</span>)}</div>
            {c.note && <p className="ml-3 mt-1 text-xs text-neutral-500">{c.note}</p>}</div>
        ))}</div>
      </Section>
      <Section title="Application Structure" testid="arch-structure"><Findings items={a.app_structure} /></Section>
      <Section title="Navigation" testid="arch-navigation"><Findings items={a.navigation} /></Section>
      <Section title="Frameworks" testid="arch-frameworks"><TechList items={a.frameworks} /></Section>
      <Section title="External Integrations" testid="arch-integrations"><TechList items={a.integrations} /></Section>
    </div>
  );
};

const Swatches = ({ title, items }) => (
  <div>
    <p className="rea-label mb-2">{title}</p>
    <div className="flex flex-wrap gap-2">{(items || []).slice(0, 12).map((c, i) => (
      <div key={i} className="w-[72px]"><div className="h-10 border border-[#333]" style={{ background: c.value.split(" ")[0] }} />
        <p className="mt-1 truncate font-mono text-[10px] text-neutral-300">{c.value}</p><p className="font-mono text-[9px] text-neutral-600">×{c.count}</p></div>
    ))}</div>
  </div>
);

export const DesignTab = ({ r, ev }) => {
  const d = r.design || {};
  const t = ev?.design_tokens || {};
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Section title="Design Overview" testid="design-overview" className="lg:col-span-2">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"><Prose>{d.overview}</Prose>
          <div className="flex-none border border-[#d4af37]/40 p-3 text-center"><p className="font-display text-3xl font-bold text-[#d4af37]">{d.consistency_score}/10</p><p className="rea-label">consistency</p></div></div>
        {d.consistency_notes && <p className="mt-3 text-sm text-neutral-400">{d.consistency_notes}</p>}
        {d.design_language && <p className="mt-3 font-ornate text-base text-[#d4af37]">{d.design_language}</p>}
      </Section>
      {d.grid_system && <Section title="Grid System" testid="design-grid"><Findings items={[d.grid_system]} />{d.type_scale_notes && <p className="mt-3 text-sm text-neutral-400"><span className="rea-label mr-2">Type scale</span>{d.type_scale_notes}</p>}</Section>}
      {d.motion?.length > 0 && <Section title="Motion & Animation" testid="design-motion"><Findings items={d.motion} /></Section>}
      {d.iconography_imagery?.length > 0 && <Section title="Iconography & Imagery" testid="design-iconography" className="lg:col-span-2"><Findings items={d.iconography_imagery} /></Section>}
      <Section title="Color Palette (interpreted)" testid="design-palette">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{(d.palette || []).map((p, i) => (
          <div key={i} className="flex items-center gap-3 border border-[#1f1f1f] p-2">
            <div className="h-10 w-10 flex-none border border-[#333]" style={{ background: p.hex }} />
            <div className="min-w-0 flex-1"><p className="font-mono text-xs text-white">{p.hex} · {p.role}</p><p className="truncate text-xs text-neutral-500">{p.usage}</p></div>
            <EvidenceBadge label={p.label} />
          </div>
        ))}</div>
      </Section>
      <Section title="Measured Computed Colors (Verified)" testid="design-measured-colors">
        <div className="space-y-4"><Swatches title="Text colors" items={t.text_colors} /><Swatches title="Backgrounds" items={t.backgrounds} /></div>
      </Section>
      <Section title="Typography" testid="design-typography">
        <div className="space-y-3">{(d.typography || []).map((ty, i) => (
          <div key={i} className="border-b border-[#1a1a1a] pb-3 last:border-0">
            <div className="flex items-start justify-between gap-2"><p className="text-xl text-white" style={{ fontFamily: ty.family }}>{ty.family}</p><EvidenceBadge label={ty.label} /></div>
            <p className="font-mono text-xs text-neutral-400">{ty.role} · sizes {ty.sizes} · weights {ty.weights}</p>
          </div>
        ))}</div>
        <p className="rea-label mb-2 mt-4">Measured font-size frequency</p>
        <div className="flex flex-wrap gap-1.5">{(t.font_sizes || []).map((f) => <span key={f.value} className="border border-[#262626] px-2 py-0.5 font-mono text-[11px] text-neutral-300">{f.value} <span className="text-neutral-600">×{f.count}</span></span>)}</div>
      </Section>
      <Section title="Spacing & Layout" testid="design-spacing">
        <Findings items={d.spacing_layout} />
        <p className="rea-label mb-2 mt-4">Measured spacing scale</p>
        <div className="space-y-1">{(t.spacing || []).slice(0, 10).map((s) => (
          <div key={s.value} className="flex items-center gap-2"><span className="w-14 font-mono text-[11px] text-neutral-400">{s.value}</span><div className="h-2 bg-red-800" style={{ width: `${Math.min(100, parseFloat(s.value) * 2)}%` }} /><span className="font-mono text-[10px] text-neutral-600">×{s.count}</span></div>
        ))}</div>
      </Section>
      <Section title="Component Styling" testid="design-components"><Findings items={d.component_styling} />
        <p className="rea-label mb-2 mt-4">Measured radii</p><div className="flex flex-wrap gap-2">{(t.radii || []).slice(0, 8).map((x) => <span key={x.value} className="border border-[#d4af37]/50 px-2 py-1 font-mono text-[10px]" style={{ borderRadius: x.value.split(" ")[0] }}>{x.value}</span>)}</div>
      </Section>
      <Section title="Responsive Design" testid="design-responsive"><Findings items={d.responsive} /></Section>
      <Section title="Images & Assets" testid="design-assets"><Findings items={d.assets} /></Section>
      <Section title="UX Patterns" testid="design-ux"><Findings items={d.ux_patterns} /></Section>
      <Section title="Page-by-page Design Notes" testid="design-pages">
        <div className="space-y-3">{(d.page_by_page || []).map((p, i) => <div key={i}><p className="font-mono text-sm text-[#d4af37]">{p.page}</p><p className="text-sm text-neutral-300">{p.notes}</p></div>)}</div>
      </Section>
      {t.css_variables?.length > 0 && (
        <Section title="CSS Custom Properties (Verified)" testid="design-css-vars" className="lg:col-span-2">
          <div className="grid max-h-64 gap-x-6 overflow-y-auto rea-scroll font-mono text-[11px] text-neutral-400 sm:grid-cols-2">{t.css_variables.map((v) => <p key={v} className="truncate">{v}</p>)}</div>
        </Section>
      )}
    </div>
  );
};

export const CodeTab = ({ r }) => {
  const c = r.code || {};
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Section title="Functionality Overview" testid="code-overview" className="lg:col-span-2">
        <Prose>{c.overview}</Prose>
        <p className="mt-3 border-l-2 border-[#d4af37] pl-3 text-xs text-neutral-500">R.E.A. analyzes client-side behavior, rendered DOM and network traffic. It does not access private server-side source code.</p>
      </Section>
      <Section title="Interactive Elements" testid="code-interactions"><Findings items={c.interactions} /></Section>
      <Section title="Forms & Workflows" testid="code-forms">
        {!c.forms_workflows?.length ? <p className="text-sm text-neutral-500">No forms detected.</p> : (
          <div className="space-y-3">{c.forms_workflows.map((f, i) => (
            <div key={i} className="border border-[#222] p-3"><div className="flex justify-between gap-2"><p className="font-mono text-sm text-white">{f.name}</p><EvidenceBadge label={f.label} confidence={f.confidence} /></div>
              <p className="mt-1 text-xs text-neutral-400">{f.purpose}</p>
              <div className="mt-2 flex flex-wrap gap-1">{(f.fields || []).map((x) => <span key={x} className="bg-[#161616] px-1.5 py-0.5 font-mono text-[10px] text-neutral-300">{x}</span>)}</div></div>
          ))}</div>
        )}
      </Section>
      <Section title="Navigation Behavior" testid="code-navigation"><Findings items={c.navigation_behavior} /></Section>
      <Section title="Component Behavior" testid="code-component-behavior"><Findings items={c.component_behavior} /></Section>
      {c.state_management?.length > 0 && <Section title="State Management" testid="code-state"><Findings items={c.state_management} /></Section>}
      {c.api_surface?.length > 0 && (
        <Section title="Observed API Surface" testid="code-api-surface">
          <div className="space-y-1.5">{c.api_surface.map((x, i) => (
            <div key={i} className="grid grid-cols-[52px_1fr_auto] items-start gap-2 border-b border-[#161616] py-1.5">
              <span className="font-mono text-[10px] font-bold text-red-400">{x.method}</span>
              <div className="min-w-0"><p className="truncate font-mono text-xs text-white">{x.endpoint}</p><p className="text-xs text-neutral-500">{x.purpose}</p></div>
              <EvidenceBadge label={x.label} confidence={x.confidence} />
            </div>
          ))}</div>
        </Section>
      )}
      <Section title="Technical Fingerprints" testid="code-fingerprints" className="lg:col-span-2">
        <div className="space-y-2">{(c.fingerprints || []).map((f, i) => (
          <div key={i} className="grid gap-1 border-b border-[#1a1a1a] pb-2 sm:grid-cols-[1fr_1.4fr_auto] sm:gap-4">
            <code className="text-xs text-red-400">{f.signal}</code><span className="text-sm text-neutral-300">{f.meaning}</span><EvidenceBadge label={f.label} confidence={f.confidence} />
          </div>
        ))}</div>
      </Section>
      <Section title="Dependencies" testid="code-dependencies"><TechList items={c.dependencies} /></Section>
      <Section title="Limitations & Unknowns" testid="code-limitations"><Findings items={c.limitations} /></Section>
    </div>
  );
};
